import { useMemo } from 'react';
import { IqName } from '@/components/IqName';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Badge, Button, Card, Stat } from '@/components/ui';
import { Icon } from '@/lib/icons';
import { Link } from 'react-router-dom';
import { getClientTeam, getLeads, getOpenGapSets, getOverviewSnapshot, getReviews, getTopics, getTraffic, qk, type OverviewSnapshot, type ReviewsPayload, type TrafficPayload } from '@/data/client';
import type { Lead } from '@/data/types';

// Referral is shown as Organic (both are non-paid), consistent with leadIQ.
const SOURCE_LABEL: Record<string, string> = { organic: 'Organic', paid: 'Paid', referral: 'Organic', ai: 'AI' };
function sourceLabel(s: string | null): string {
  return (s && SOURCE_LABEL[s]) || '—';
}
function fmtWhen(days: number): string {
  if (days >= 9999) return '—';
  if (days === 0) return 'Today';
  if (days === 1) return 'Yesterday';
  if (days < 30) return `${days}d ago`;
  return `${Math.round(days / 7)}w ago`;
}

interface KpiCard {
  label: string;
  value: string;
  sub?: string;
}

/* Headline KPIs pulled across sources: total leads (CRM), conversions +
   conversion rate (GA4, last 30d), and new reviews (last 30d). */
function buildKpis(leads: Lead[], traffic?: TrafficPayload, reviews?: ReviewsPayload): KpiCard[] {
  const k = traffic?.configured ? traffic.kpis : undefined;
  const conversions = k ? k.conversions.cur : null;
  const sessions = k ? k.sessions.cur : null;
  const convRate = k && sessions ? (k.conversions.cur / sessions) * 100 : null;
  const newReviews = reviews?.configured ? reviews.inRange ?? 0 : null;
  const leads30 = leads.filter((l) => l.days <= 30).length;

  return [
    { label: 'Total leads', value: leads30.toLocaleString(), sub: 'last 30 days' },
    { label: 'Conversions', value: conversions != null ? Math.round(conversions).toLocaleString() : '—', sub: 'GA4 · last 30 days' },
    { label: 'Conversion rate', value: convRate != null ? `${convRate.toFixed(1)}%` : '—', sub: 'conversions / sessions' },
    { label: 'New reviews', value: newReviews != null ? String(newReviews) : '—', sub: 'last 30 days' },
  ];
}

/* Leads per month, last 12 months, from receivedAt. */
function computePerformance(leads: Lead[]): { label: string; value: number }[] {
  const now = new Date();
  const buckets = Array.from({ length: 12 }, (_, i) => {
    const d = new Date(now.getFullYear(), now.getMonth() - (11 - i), 1);
    return { key: `${d.getFullYear()}-${d.getMonth()}`, label: 'JFMAMJJASOND'[d.getMonth()], value: 0 };
  });
  const byKey = new Map(buckets.map((b) => [b.key, b]));
  for (const l of leads) {
    if (!l.receivedAt) continue;
    const d = new Date(l.receivedAt);
    if (Number.isNaN(d.getTime())) continue;
    const b = byKey.get(`${d.getFullYear()}-${d.getMonth()}`);
    if (b) b.value += 1;
  }
  return buckets.map(({ label, value }) => ({ label, value }));
}

interface Slice { name: string; pct: number; color: string }

const CHANNELS = [
  { key: 'phone', name: 'Phone', color: 'var(--triton-blue)' },
  { key: 'chat', name: 'Chat', color: 'var(--dark-700)' },
  { key: 'email', name: 'Email', color: 'var(--slate-400)' },
];
function computeChannels(leads: Lead[]): Slice[] {
  const total = leads.length || 1;
  const slices = CHANNELS.map((c) => ({ name: c.name, color: c.color, count: leads.filter((l) => l.channel === c.key).length }));
  const known = slices.reduce((s, x) => s + x.count, 0);
  const other = leads.length - known;
  if (other > 0) slices.push({ name: 'Other', color: 'var(--cream-400)', count: other });
  return slices.filter((s) => s.count > 0).map((s) => ({ name: s.name, color: s.color, pct: Math.round((s.count / total) * 100) }));
}

const SOURCES = [
  { key: 'paid', name: 'Paid', color: 'var(--triton-blue)' },
  { key: 'organic', name: 'Organic', color: 'var(--dark-700)' }, // includes referral
  { key: 'ai', name: 'AI', color: 'var(--success)' },
];
function computeSources(leads: Lead[]): Slice[] {
  const norm = (s: string | null) => (s === 'referral' ? 'organic' : s);
  const total = leads.length || 1;
  const slices = SOURCES.map((c) => ({ name: c.name, color: c.color, count: leads.filter((l) => norm(l.source) === c.key).length }));
  const known = slices.reduce((s, x) => s + x.count, 0);
  const other = leads.length - known;
  if (other > 0) slices.push({ name: 'Other', color: 'var(--cream-400)', count: other });
  return slices.filter((s) => s.count > 0).map((s) => ({ name: s.name, color: s.color, pct: Math.round((s.count / total) * 100) }));
}

const AREA_META: Record<string, { icon: string; to: string; label: string }> = {
  leads: { icon: 'users', to: '/leads', label: 'leadIQ' },
  traffic: { icon: 'trending-up', to: '/traffic', label: 'trafficIQ' },
  campaigns: { icon: 'target', to: '/campaigns', label: 'campaignIQ' },
  reviews: { icon: 'star', to: '/reviews', label: 'reviewsIQ' },
  content: { icon: 'bar-chart', to: '/content', label: 'contentIQ' },
};
const DELIVERABLE_META: Record<string, { icon: string; to: string; cta: string }> = {
  content: { icon: 'sparkles', to: '/content', cta: 'Start an interview' },
  reviews: { icon: 'star', to: '/reviews', cta: 'View reviews' },
};

/* Cross-section AI snapshot — a quick pulse across all areas (observational),
   plus the two client deliverables (content, reviews) when warranted. Links to
   each section for detail rather than repeating their analysis. Cached 24h. */
function AiSnapshot() {
  const qc = useQueryClient();
  const { data, isLoading } = useQuery({ queryKey: qk.overviewSnapshot, queryFn: () => getOverviewSnapshot(), staleTime: 5 * 60_000 });
  const refresh = useMutation({ mutationFn: () => getOverviewSnapshot(true), onSuccess: (d) => qc.setQueryData(qk.overviewSnapshot, d) });
  const d = data as OverviewSnapshot | undefined;

  if (isLoading) return <Card padding="lg" className="ti-panel"><p className="dash-muted">Reading your dashboard…</p></Card>;
  if (!d || !d.configured || d.empty) return null;
  if (d.error) return null;

  return (
    <Card padding="lg" className="ti-panel">
      <div className="ti-head">
        <div className="ti-title">
          <span className="ti-mark"><Icon name="sparkles" size={16} /></span>
          <div><h2>AI snapshot</h2><p className="dash-muted">A quick pulse across everything</p></div>
        </div>
        <Button variant="ghost" size="sm" iconLeft={<Icon name="refresh-cw" size={14} />} disabled={refresh.isPending} onClick={() => refresh.mutate()}>
          {refresh.isPending ? 'Refreshing…' : 'Refresh'}
        </Button>
      </div>

      {d.summary && <p className="ti-summary">{d.summary}</p>}

      {(d.highlights?.length ?? 0) > 0 && (
        <ul className="ov-snap">
          {d.highlights!.map((h, i) => {
            const m = AREA_META[h.area] ?? AREA_META.leads;
            return (
              <li key={i}>
                <span className="ov-snap__icon"><Icon name={m.icon} size={15} /></span>
                <span className="ov-snap__text">{h.text}</span>
                <Link className="ov-snap__link" to={m.to}>{m.label} →</Link>
              </li>
            );
          })}
        </ul>
      )}

      {(d.deliverables?.length ?? 0) > 0 && (
        <div className="ov-deliver">
          <h3 className="ov-deliver__title">How you can help</h3>
          {d.deliverables!.map((dv, i) => {
            const m = DELIVERABLE_META[dv.kind];
            return (
              <div key={i} className="ov-deliver__item">
                <span className="ov-deliver__icon"><Icon name={m.icon} size={15} /></span>
                <p>{dv.text}</p>
                <Link className="tr-btn tr-btn--ghost tr-btn--sm" to={m.to}>{m.cta}</Link>
              </div>
            );
          })}
        </div>
      )}

      {d.generatedAt && <p className="ti-stamp">Snapshot from {snapAgo(d.generatedAt)} · updates daily · Refresh for the latest</p>}
    </Card>
  );
}
function snapAgo(iso?: string): string {
  if (!iso) return '';
  const mins = Math.max(0, Math.round((Date.now() - new Date(iso).getTime()) / 60000));
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins} min ago`;
  const h = Math.floor(mins / 60);
  return h < 24 ? `${h}h ago` : `${Math.floor(h / 24)}d ago`;
}

function KpiRow({ kpis }: { kpis: KpiCard[] }) {
  return (
    <div className="dash-kpis">
      {kpis.map((k) => (
        <Card key={k.label} padding="lg"><Stat label={k.label} value={k.value} sub={k.sub} /></Card>
      ))}
    </div>
  );
}

function PerformancePanel({ data }: { data: { label: string; value: number }[] }) {
  const max = Math.max(1, ...data.map((d) => d.value));
  return (
    <Card padding="lg">
      <div className="dash-panelhead"><div><h2>Performance</h2><p>Leads per month · last 12 months</p></div></div>
      <div className="dash-chart">
        {data.map((d, i) => (
          <div key={i} className="dash-chart__col">
            <div className="dash-chart__bar" style={{ height: `${(d.value / max) * 100}%` }}><span>{d.value}</span></div>
            <em>{d.label}</em>
          </div>
        ))}
      </div>
    </Card>
  );
}

/* Shared breakdown panel — used for both "Leads by channel" and "Leads by source". */
function BreakdownPanel({ title, subtitle, data }: { title: string; subtitle: string; data: Slice[] }) {
  return (
    <Card padding="lg">
      <div className="dash-panelhead"><div><h2>{title}</h2><p>{subtitle}</p></div></div>
      {data.length === 0 ? (
        <p className="dash-muted">No leads yet.</p>
      ) : (
        <>
          <div className="dash-chan__bar">
            {data.map((c) => <span key={c.name} style={{ width: `${c.pct}%`, background: c.color }} />)}
          </div>
          <ul className="dash-chan__list">
            {data.map((c) => (
              <li key={c.name}>
                <span className="dash-chan__dot" style={{ background: c.color }} />
                <span className="dash-chan__name">{c.name}</span>
                <span className="dash-chan__pct">{c.pct}%</span>
              </li>
            ))}
          </ul>
        </>
      )}
    </Card>
  );
}

function RecentLeads({ leads }: { leads: Lead[] }) {
  const rows = [...leads].sort((a, b) => a.days - b.days).slice(0, 4);
  return (
    <Card padding={false} className="dash-leads">
      <div className="dash-panelhead dash-panelhead--pad">
        <div><h2>Recent leads</h2><p>Latest activity</p></div>
        <Button variant="ghost" size="sm" iconRight={<Icon name="arrow-right" size={15} />}>View all</Button>
      </div>
      <table className="dash-table">
        <thead><tr><th>Customer</th><th>Source</th><th>Est. value</th><th>Received</th></tr></thead>
        <tbody>
          {rows.map((l) => (
            <tr key={l.uid}>
              <td><div className="dash-cust"><span>{l.name}</span></div></td>
              <td><Badge variant={l.source === 'paid' ? 'accent' : l.source === 'ai' ? 'success' : 'neutral'}>{sourceLabel(l.source)}</Badge></td>
              <td className="dash-val">{l.value ? `$${l.value.toLocaleString()}` : '—'}</td>
              <td className="dash-time">{fmtWhen(l.days)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </Card>
  );
}

/* Stale-reviews alert only — the review count now lives in the KPIs. */
function ReviewsStaleAlert({ reviews }: { reviews?: ReviewsPayload }) {
  if (!reviews?.configured || !reviews.isStale) return null;
  return (
    <Card padding="lg" className="rv-alert">
      <span className="rv-alert__icon"><Icon name="alert-circle" size={20} /></span>
      <div>
        <strong>Reviews have gone quiet — {reviews.staleDays} days since the last one</strong>
        <p>Past the {reviews.staleThreshold}-day threshold. <Link to="/reviews">Open reviewsIQ</Link> to review, or line up a review-request push.</p>
      </div>
    </Card>
  );
}

export function Overview() {
  const { data: leads = [] } = useQuery({ queryKey: qk.leads, queryFn: getLeads });
  const { data: traffic } = useQuery({ queryKey: ['traffic', '30d', 'all'], queryFn: () => getTraffic('30d', 'all'), staleTime: 5 * 60_000 });
  const { data: reviews } = useQuery({ queryKey: [...qk.reviews, '30d'], queryFn: () => getReviews('30d'), staleTime: 5 * 60_000 });

  const kpis = useMemo(() => buildKpis(leads, traffic, reviews), [leads, traffic, reviews]);
  const perf = useMemo(() => computePerformance(leads), [leads]);
  const channels = useMemo(() => computeChannels(leads), [leads]);
  const sources = useMemo(() => computeSources(leads), [leads]);

  return (
    <div className="dash-stack">
      <ReviewsStaleAlert reviews={reviews} />
      <OpenQuestionsCard />
      <TopicIdeasCard />
      <KpiRow kpis={kpis} />
      <AiSnapshot />
      <div className="dash-cols dash-cols--wide">
        <RecentLeads leads={leads} />
        <BreakdownPanel title="Leads by channel" subtitle="All leads" data={channels} />
      </div>
      <div className="dash-cols">
        <PerformancePanel data={perf} />
        <BreakdownPanel title="Leads by source" subtitle="All leads" data={sources} />
      </div>
      <TeamPanel />
    </div>
  );
}

function TeamPanel() {
  const { data } = useQuery({ queryKey: ['clientTeam'], queryFn: getClientTeam, staleTime: 10 * 60_000 });
  const team = data?.team ?? [];
  if (!team.length) return null;
  return (
    <Card padding="lg" className="dash-team">
      <h2 className="dash-team__title"><Icon name="users" size={17} /> Your Triton team</h2>
      <div className="dash-team__grid">
        {team.map((m, i) => (
          <div key={i} className="dash-team__member">
            {m.imageUrl
              ? <img src={m.imageUrl} alt={m.name} className="dash-team__avatar" />
              : <div className="dash-team__avatar dash-team__avatar--ph" aria-hidden="true">{m.name.charAt(0)}</div>}
            <div className="dash-team__meta">
              <span className="dash-team__name">{m.name}</span>
              <span className="dash-team__role"><IqName text={m.role} /></span>
            </div>
          </div>
        ))}
      </div>
    </Card>
  );
}

/* Questions Triton is waiting on, at the top of the dashboard — the email can
   be missed; the dashboard is where the client already is. Links to the
   answer section in contentIQ. Hidden when nothing is open. */
function OpenQuestionsCard() {
  const { data } = useQuery({ queryKey: ['gaps', 'open'], queryFn: getOpenGapSets, staleTime: 60_000 });
  const items = data?.items ?? [];
  if (!items.length) return null;
  const pending = items.reduce((n, s) => n + s.pending, 0);
  return (
    <Card padding="lg" className="rv-alert">
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
        <Icon name="message-circle" size={18} />
        <div style={{ flex: 1, minWidth: 220 }}>
          <strong>{pending} question{pending === 1 ? '' : 's'} waiting for your expertise</strong>
          <div className="dash-muted" style={{ fontSize: 'var(--text-sm)' }}>
            {items.map((s) => s.short).join(' · ')} — about a minute each, and you can dictate.
          </div>
        </div>
        <Link to="/content"><Button size="sm" variant="primary">Answer now</Button></Link>
      </div>
    </Card>
  );
}

/* Article ideas Triton suggested that the client has not started. Same place
   and shape as the open-questions notice, because to the client they are the
   same thing: something small that is waiting on them. Each link lands in
   contentIQ on that topic's interview with the form filled. */
function TopicIdeasCard() {
  const { data } = useQuery({ queryKey: ['topics'], queryFn: getTopics, staleTime: 60_000 });
  const items = (data ?? []).filter((t) => t.status === 'suggested');
  if (!items.length) return null;
  return (
    <Card padding="lg" className="rv-alert">
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
        <Icon name="sparkles" size={18} />
        <div style={{ flex: 1, minWidth: 220 }}>
          <strong>{items.length === 1 ? 'An article idea is waiting for you' : `${items.length} article ideas are waiting for you`}</strong>
          <div className="dash-muted" style={{ fontSize: 'var(--text-sm)' }}>
            {items.slice(0, 3).map((t) => t.title).join(' · ')}{items.length > 3 ? ` · and ${items.length - 3} more` : ''} — a ten-minute interview each, by voice if you like.
          </div>
        </div>
        <Link to={`/content?topic=${encodeURIComponent(items[0].id)}`}><Button size="sm" variant="primary">Start</Button></Link>
      </div>
    </Card>
  );
}
